import { Box, Chip, Collapse, Divider, IconButton } from "@mui/material"
import { DeleteOutlined, KeyboardArrowDownOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useCallback, useState } from "react"
import type { UseFormReturn } from "react-hook-form"

import type { AddedProduct, SalesCustomer } from "../../types"

import { PRODUCT_BADGE_BY_PATH } from "./pricing/constants"
import { ProductPricingForm } from "./pricing/ProductPricingForm"
import type { PricingFormValues } from "./pricing/schemas"
import { PricingPath } from "./pricing/types"
import { ProductCard } from "./ProductCard"

import { AerosCheckbox } from "@/components/UiKit"

interface AddedProductCardProps {
  addedProduct: AddedProduct
  customer: SalesCustomer
  initialPricingValues?: PricingFormValues
  onRemove: () => void
  onRequestQuoteChange: (checked: boolean) => void
  onRegisterMethods: (productId: number, methods: UseFormReturn<PricingFormValues>) => void
  onUnregisterMethods: (productId: number) => void
  onMissingListPriceChange?: (productId: number, isMissing: boolean) => void
}

export const AddedProductCard: FC<AddedProductCardProps> = ({
  addedProduct,
  customer,
  initialPricingValues,
  onRemove,
  onRequestQuoteChange,
  onRegisterMethods,
  onUnregisterMethods,
  onMissingListPriceChange,
}) => {
  const { product, requestQuote } = addedProduct
  const [pricingPath, setPricingPath] = useState<PricingPath>(initialPricingValues?.path ?? PricingPath.List)
  const badge = PRODUCT_BADGE_BY_PATH[pricingPath]
  const isExpanded = requestQuote

  const handleMissingListPriceChange = useCallback(
    (isMissing: boolean) => onMissingListPriceChange?.(product.productId, isMissing),
    [product.productId, onMissingListPriceChange]
  )

  return (
    <Box className='flex flex-col gap-3 rounded border border-gray-400 p-3'>
      <Box className='sticky -top-6 z-10 -mx-3 -mt-3 flex items-start gap-2 rounded-t border-b border-gray-400 bg-white p-4'>
        <ProductCard product={product} />
        <IconButton
          size='small'
          onClick={onRemove}
          className='shrink-0'
          aria-label='Remove product'
          data-testid={`sales.added-product-card.remove-button-${product.productId}`}
        >
          <DeleteOutlined />
        </IconButton>
      </Box>

      {isExpanded && (
        <>
          <Box className='flex flex-wrap gap-2'>
            <Chip
              label={badge.label}
              size='small'
              className={`rounded ${badge.className}`}
              data-testid={`sales.added-product-card.pricing-path-badge-${product.productId}`}
            />
          </Box>
          <Divider />
        </>
      )}

      <Box className='flex flex-col gap-1'>
        <AerosCheckbox
          checked={requestQuote}
          onChange={onRequestQuoteChange}
          size='small'
          label='Request Quote'
          data-testid={`sales.added-product-card.request-quote-checkbox-${product.productId}`}
        />
      </Box>

      <Collapse in={requestQuote} unmountOnExit>
        <ProductPricingForm
          product={product}
          customerId={customer.company.companyId}
          initialValues={initialPricingValues}
          onRegisterMethods={onRegisterMethods}
          onUnregisterMethods={onUnregisterMethods}
          onPathChange={setPricingPath}
          onMissingListPriceChange={handleMissingListPriceChange}
        />
      </Collapse>

      <Box className='flex flex-col gap-1'>
        <AerosCheckbox disabled size='small' label='Request Sample' />
        <AerosCheckbox disabled size='small' label='Request Documents' />
      </Box>

      {!isExpanded && (
        <Box
          className='flex justify-center text-gray-500'
          data-testid={`sales.added-product-card.collapsed-indicator-${product.productId}`}
        >
          <KeyboardArrowDownOutlined />
        </Box>
      )}
    </Box>
  )
}
